/**
 * Notices sent — the global audit log of encroachment notifications.
 * Every draft/send across all cases, with delivery + open tracking rolled up
 * from Resend webhook/poll events. Click a row to open its case.
 */

import { useEffect, useState } from 'react';
import { Icon } from '../components/Icon';
import { NoticeDetailModal } from '../components/NoticeDetailModal';
import { api } from '../lib/api';

interface RecipientSummary {
  email: string;
  role: 'to' | 'cc' | 'test';
  status: string;
  delivered_at: string | null;
  opened_at: string | null;
}

interface LogRow {
  id: number;
  batch_id: number | null;
  case_id: number;
  party: 'offending' | 'neighbor' | null;
  to_emails: string[];
  cc_emails: string[];
  subject: string | null;
  status: string;
  test_mode: boolean;
  sent_to: string[] | null;
  sent_at: string | null;
  delivered_at: string | null;
  opened_at: string | null;
  created_at: string;
  actor_email: string | null;
  franchisee_af: string | null;
  owner_af: string | null;
  case_state: string | null;
  recipients: RecipientSummary[];
}

const STATUS_META: Record<string, { dot: string; label: string }> = {
  draft: { dot: 'warn', label: 'Draft' },
  queued: { dot: 'warn', label: 'Queued' },
  sent: { dot: 'ok', label: 'Sent' },
  delivered: { dot: 'ok', label: 'Delivered' },
  opened: { dot: 'ok', label: 'Opened' },
  bounced: { dot: 'danger', label: 'Bounced' },
  failed: { dot: 'danger', label: 'Failed' },
};

const FILTERS = ['all', 'draft', 'sent', 'delivered', 'opened', 'bounced', 'failed'];

export function NotificationsLog() {
  const [rows, setRows] = useState<LogRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState('all');
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [openNotice, setOpenNotice] = useState<number | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const LIMIT = 100;

  useEffect(() => {
    setRows(null);
    api<{ notices: LogRow[]; total: number }>(`/api/notifications/log?status=${filter}&limit=${LIMIT}&offset=${offset}`)
      .then((d) => { setRows(d.notices); setTotal(d.total); })
      .catch((e) => setError(e instanceof Error ? e.message : 'load failed'));
  }, [filter, offset, reloadKey]);

  return (
    <div className="scroll-y" style={{ flex: 1, padding: 24, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Operate</div>
          <h1 style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 600 }}>Notices sent</h1>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
            Every encroachment notice across all cases — who it went to, when, and whether it was
            delivered and opened. TEST rows went to the test recipients only.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {FILTERS.map((f) => (
            <button
              key={f}
              className="btn btn-sm"
              onClick={() => { setFilter(f); setOffset(0); }}
              style={{
                background: filter === f ? 'var(--accent)' : 'var(--surface-2)',
                color: filter === f ? '#fff' : 'var(--text)',
                textTransform: 'capitalize',
              }}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="card card-pad c-danger" style={{ fontSize: 13 }}>{error}</div>}
      {!rows && !error && <div className="card card-pad muted" style={{ fontSize: 13 }}>Loading…</div>}

      {rows && (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                {['Notice', 'Status', 'Party', 'To', 'Subject', 'Sent', 'Delivered', 'Opened', 'By'].map((h) => (
                  <th key={h} className="muted" style={{ padding: '10px 12px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={9} className="muted" style={{ padding: 16, fontSize: 13 }}>No notices{filter !== 'all' ? ` with status ${filter}` : ' yet'}.</td></tr>
              )}
              {rows.map((r) => {
                const meta = STATUS_META[r.status] ?? { dot: 'warn', label: r.status };
                return (
                  <tr
                    key={r.id}
                    style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                    onClick={() => setOpenNotice(r.id)}
                    title="View notice details"
                  >
                    <td className="mono" style={{ padding: '9px 12px', whiteSpace: 'nowrap' }} title={r.batch_id != null ? `Batch #${r.batch_id}` : undefined}>
                      #{r.id}
                      <span className="faint" style={{ marginLeft: 6, fontSize: 11 }}>{r.franchisee_af}{r.owner_af && r.owner_af !== r.franchisee_af ? ` → ${r.owner_af}` : ''}</span>
                    </td>
                    <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                      <span className="pill" style={{ height: 20, fontSize: 10.5 }}>
                        <span className={`pill-dot dot-${meta.dot}`}></span>
                        {meta.label}{r.test_mode && r.status !== 'draft' ? ' (TEST)' : ''}
                      </span>
                    </td>
                    <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>{r.party === 'offending' ? 'Servicing' : r.party === 'neighbor' ? 'Owner' : '—'}</td>
                    <td style={{ padding: '9px 12px', maxWidth: 280 }} title={[...r.to_emails, ...(r.cc_emails.length ? [`cc: ${r.cc_emails.join(', ')}`] : [])].join(', ')}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.to_emails.join(', ') || '—'}
                      </div>
                      {r.recipients.length > 0 && (
                        <div style={{ fontSize: 10.5, marginTop: 2, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {summarizeRecipients(r.recipients).map(([label, dot, count]) => (
                            <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                              <span className={`pill-dot dot-${dot}`}></span>
                              <span className="muted">{count} {label}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '9px 12px', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.subject ?? ''}>{r.subject}</td>
                    <td className="muted" style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>{fmt(r.sent_at)}</td>
                    <td className="muted" style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>{fmt(r.delivered_at)}</td>
                    <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                      {r.opened_at ? <span className="c-ok">{fmt(r.opened_at)}</span> : <span className="faint">—</span>}
                    </td>
                    <td className="muted" style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>{r.actor_email ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {openNotice != null && (
        <NoticeDetailModal noticeId={openNotice} onClose={() => setOpenNotice(null)} onChanged={() => setReloadKey((k) => k + 1)} />
      )}

      {rows && total > LIMIT && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
          <button className="btn btn-sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))}>
            <Icon name="chevleft" size={12} /> Newer
          </button>
          <span className="muted" style={{ fontSize: 12 }}>{offset + 1}–{Math.min(offset + LIMIT, total)} of {total}</span>
          <button className="btn btn-sm" disabled={offset + LIMIT >= total} onClick={() => setOffset(offset + LIMIT)}>
            Older <Icon name="chevright" size={12} />
          </button>
        </div>
      )}
    </div>
  );
}

function fmt(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// Collapse per-recipient statuses into chips: "2 opened · 1 delivered · 1 bounced"
function summarizeRecipients(recs: RecipientSummary[]): [string, string, number][] {
  const dot: Record<string, string> = {
    opened: 'ok', delivered: 'ok', sent: 'warn', pending: 'warn', bounced: 'danger', failed: 'danger',
  };
  const counts = new Map<string, number>();
  for (const r of recs) counts.set(r.status, (counts.get(r.status) ?? 0) + 1);
  const order = ['opened', 'delivered', 'sent', 'pending', 'bounced', 'failed'];
  return order
    .filter((s) => counts.has(s))
    .map((s) => [s, dot[s] ?? 'warn', counts.get(s)!]);
}
